<template>
  <BLayoutView
    :back-route="backRoute"
    :title="pageTitle"
    :skeleton="isLoadingAsyncData"
    :save-menu-list="null"
  >
    <template #form>
      <ComponentForm
        ref="form"
        v-bind="viewTypeOptions"
        :data="viewTypeOptions.isCreate ? void 0 : single"
        :loading="isLoading"
        :editable="editable"
        :skeleton="isLoadingAsyncData"
        class="my-4"
        @save="onSave"
        @delete="onDelete(single)"
        @archived="onArchived"
        @unarchived="onUnarchived"
        @redirect="onRedirect"
      >
        <template v-if="isEditable" #save-menu>
          <v-row dense class="mt-4">
            <v-spacer />
            <v-col cols="auto">
              <BFormButtons
                :edit-route="editRoute"
                :loading="isLoading"
                :save-menu-list="saveMenuList"
                :skeleton="isLoadingAsyncData"

                :saveable="saveable"
                :editable="editable"
                :deletable="deletable"

                @delete="onDelete(single)"
                @save-menu="onSave"
              />
            </v-col>
          </v-row>
        </template>
      </ComponentForm>

      <template v-if="!viewTypeOptions.isCreate && single">
        <PublicationsCuratorsExpansionTable
          v-if="!single.is_public"
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />

        <PublicationParticipantsTable
          v-if="!single.is_public"
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />

        <PublicationStorageList
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />

        <PublicationDataCollectionsList
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />

        <PublicationMunMehList
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />
      </template>
    </template>
  </BLayoutView>
</template>

<script>
import axios from 'axios'
import { createModalConfirm } from '@/helpers/createModalConfirm'
import { createViewMixin } from '@/helpers/mixins/createViewMixin'
import { createDeletableMixin } from '@/helpers/mixins/createDeletableMixin'
import { lkPublicationPermissions as Permissions } from '@/helpers/enums/permission-models'
import { PublicationModel as Model } from './Model'
import {
  publicationsStoreModule as StoreModule,
  publicationsMapper as StoreMapper,
} from './store'
import { PUBLICATIONS_ROUTES as ROUTES } from './utils'

import BLayoutView from '@/components/layouts/BLayoutView.vue'
import BFormButtons, { SAVE_MENUS } from '@/components/common/BFormButtons.vue'
import ComponentForm from './components/PublicationsForm.vue'
import * as VIEW_TYPES from '@/helpers/enums/view-types'

const PublicationsCuratorsExpansionTable = () => import(
  /* webpackChunkName: "PublicationsCuratorsExpansionTable" */
  './Curators/PublicationsCuratorsExpansionTable.vue'
)


const PublicationParticipantsTable = () => import(
  /* webpackChunkName: "PublicationsParticipantsTable" */
  './Participants/PublicationsParticipantsExpansionTable.vue'
)

const PublicationStorageList = () => import(
  /* webpackChunkName: "PublicationStorageList" */
  './Storage/PublicationStorageList.vue'
)

const PublicationDataCollectionsList = () => import(
  /* webpackChunkName: "PublicationDataCollectionsList" */
  './DataCollections/PublicationDataCollectionsList.vue'
)

const PublicationMunMehList = () => import(
  /* webpackChunkName: "PublicationMunMehList" */
  './MunMeh/PublicationMunMehList.vue'
)

const mixinOptions = { Model, StoreModule, ROUTES }
const viewMixin = createViewMixin(mixinOptions)
const deletableMixin = createDeletableMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createViewMixin').ViewMixin<any, StoreModule> } View */
/** @typedef { import('@/helpers/mixins/createDeletableMixin').DeletableMixin<any, StoreModule> } Deletable */

export default {
  name: 'ViewPublicationsView',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BLayoutView,
    BFormButtons,
    ComponentForm,
    PublicationStorageList,
    PublicationParticipantsTable,
    PublicationDataCollectionsList,
    PublicationMunMehList,
    PublicationsCuratorsExpansionTable,
  },
  mixins: [
    viewMixin,
    deletableMixin,
  ],
  data: () => ({
    backRoute: { name: ROUTES.LIST },
    saveMenuList: Object.values(SAVE_MENUS),
  }),
  computed: {
    viewTypeOptions() {
      const { viewType } = this
      return !this.$access(Permissions.edit)
        ? {
          isCreate: false,
          isEdit: false,
          isView: true,
        }
        : {
          isCreate: viewType === VIEW_TYPES.CREATE,
          isEdit: viewType === VIEW_TYPES.EDIT,
          isView: viewType === VIEW_TYPES.VIEW,
        }
    },
    isEditable() {
      return this.$access(Permissions.edit)
    },
    createRoute() {
      return {
        name: ROUTES.CREATE,
      }
    },
    editRoute() {
      return {
        name: ROUTES.EDIT,
        props: { id: this.id },
      }
    },
  },
  /** @type { View & Deletable } */
  methods: {
    ...StoreMapper.mapActions([
      'archive',
      'unarchive',
      'redirect',
      'getDefaultRedirect',
    ]),

    async onSave(type) {
      const form = await this.$refs.form.onSubmit()
      const result = await this.onSubmit(form)
      // this.$refs.form.onReset()

      const { id } = result
      const editRoute = { name: ROUTES.EDIT, params: { id } }
      this.toSave(this.backRoute, this.createRoute, editRoute, type)
    },
    async onDelete(data) {
      const result = await this.toDelete(data)
      if (!result) return
      this.$router.replace(this.backRoute)
    },
    async onArchived(callback) {
      const watched = callback?.()
      const options = { id: this.id }

      try {
        await this.archive(options)
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$notice.errors(e)
        }
      } finally {
        watched?.()
      }
    },
    async onUnarchived(callback) {
      const watched = callback?.()
      const options = { id: this.id }

      try {
        await this.unarchive(options)
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$notice.errors(e)
        }
      } finally {
        watched?.()
      }
    },
    async onRedirect(callback) {
      const watched = callback?.()
      const options = {
        id: this.id,
      }

      const { data: defaultPublication } = await this.getDefaultRedirect().catch(() => ({}))

      const propsData = {
        successText: 'Установка перенаправления успешна',
        errorText: 'Ошибка установки перенаправления',
        mainText: `
          Убрать перенаправление с
          "${defaultPublication?.name}"
          и установить на текущую публикацию?
        `,
        action: () => this.redirect(options),
      }

      if (defaultPublication && defaultPublication.id !== +this.id) {
        watched?.()
        const modal = await createModalConfirm(this, propsData)
        modal.open()
        return
      }

      try {
        await this.redirect(options)
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$notice.errors(e)
        }
      } finally {
        watched?.()
      }
    },
  },
}
</script>
